/**
 * Proactively refreshes (or expires) the session before the access JWT's
 * `exp` is reached, instead of waiting for some unrelated request to 401.
 * Mounted once from AuthProvider — see its doc comment for why that means
 * this runs on every host this SPA serves (pro/account/certification).
 */
import * as React from "react";
import { useAtomValue } from "jotai";
import { accessTokenAtom, userPayloadAtom } from "./atoms";
import { useAuth } from "./hooks";
import { handleSessionExpired } from "./session-expired";

/** Fire a little before the literal exp so a proactive refresh always beats a race with an in-flight request. */
const EARLY_REFRESH_SKEW_MS = 5_000;
/** Coarse safety net in case the primary timer and visibility listener both miss (e.g. unusual background throttling). */
const FALLBACK_POLL_MS = 60_000;

export function useSessionExpiryTimer(): void {
  const accessToken = useAtomValue(accessTokenAtom);
  const userPayload = useAtomValue(userPayloadAtom);
  const { refreshAccessToken } = useAuth();

  // Always-current check, callable from timer/visibility/poll without re-subscribing them.
  const checkAndActRef = React.useRef<() => void>(() => {});
  React.useEffect(() => {
    checkAndActRef.current = () => {
      if (!accessToken || !userPayload) return;
      const msRemaining = userPayload.exp * 1000 - Date.now();
      if (msRemaining > EARLY_REFRESH_SKEW_MS) return;
      void refreshAccessToken().catch(() => {
        handleSessionExpired();
      });
    };
  }, [accessToken, userPayload, refreshAccessToken]);

  // Primary: fire at (just before) literal expiry. Re-armed whenever the
  // token changes (login/refresh both update userPayloadAtom's exp).
  React.useEffect(() => {
    if (!accessToken || !userPayload) return;
    const msUntilCheck = userPayload.exp * 1000 - Date.now() - EARLY_REFRESH_SKEW_MS;
    const timer = window.setTimeout(() => checkAndActRef.current(), Math.max(0, msUntilCheck));
    return () => window.clearTimeout(timer);
  }, [accessToken, userPayload]);

  // A background/sleeping tab can throttle or miss the primary timeout —
  // re-check the instant the tab is visible again rather than trusting it.
  React.useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") checkAndActRef.current();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Coarse fallback poll.
  React.useEffect(() => {
    const interval = window.setInterval(() => checkAndActRef.current(), FALLBACK_POLL_MS);
    return () => window.clearInterval(interval);
  }, []);
}
